<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Produits à régulariser</h1>
          <p class="text-xs text-gray-400">Gérez les régularisations de stock en attente</p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <span class="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full"
          :class="filters.pending_only ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'">
          <span class="w-1.5 h-1.5 rounded-full" :class="filters.pending_only ? 'bg-amber-400' : 'bg-gray-400'"></span>
          {{ filters.pending_only ? 'En attente seulement' : 'Tous les statuts' }}
        </span>
      </div>
    </div>

    <!-- Stats rapides -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-list text-primary text-sm"></i>
          </div>
          <div>
            <p class="text-2xl font-bold text-gray-900 leading-none">{{ pagination.total }}</p>
            <p class="text-[11px] text-gray-400 mt-0.5">Total</p>
          </div>
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-clock text-amber-500 text-sm"></i>
          </div>
          <div>
            <p class="text-2xl font-bold text-amber-600 leading-none">{{ pendingCount }}</p>
            <p class="text-[11px] text-gray-400 mt-0.5">En attente</p>
          </div>
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-check-circle text-emerald-500 text-sm"></i>
          </div>
          <div>
            <p class="text-2xl font-bold text-emerald-600 leading-none">{{ reconciledCount }}</p>
            <p class="text-[11px] text-gray-400 mt-0.5">Réconciliées</p>
          </div>
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-ban text-gray-400 text-sm"></i>
          </div>
          <div>
            <p class="text-2xl font-bold text-gray-500 leading-none">{{ cancelledCount }}</p>
            <p class="text-[11px] text-gray-400 mt-0.5">Annulées</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Table card -->
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <!-- Toolbar -->
      <div class="px-5 py-3 border-b bg-gray-50/80 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div class="flex flex-wrap items-center gap-2">
          <div class="relative">
            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
            <input
              type="text"
              v-model="searchQuery"
              @input="onSearchInput"
              placeholder="Rechercher par référence ou produit..."
              class="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300 w-64"
            />
          </div>
          <select
            v-model="filters.status"
            @change="onFiltersChange"
            class="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 text-gray-600">
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="reconciled">Réconciliée</option>
            <option value="cancelled">Annulée</option>
          </select>
        </div>
        <label class="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            v-model="filters.pending_only"
            @change="onFiltersChange"
            class="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary/30"
          />
          <span class="text-xs text-gray-500 font-medium">En attente seulement</span>
        </label>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <span class="w-8 h-8 border-3 border-gray-200 border-t-primary rounded-full animate-spin mb-3"></span>
        <p class="text-xs text-gray-400">Chargement des régularisations...</p>
      </div>

      <div v-else>
        <!-- Empty state -->
        <div v-if="regularizations.length === 0" class="py-16 text-center">
          <div class="flex flex-col items-center">
            <div class="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
              <i class="fas fa-inbox text-gray-300 text-lg"></i>
            </div>
            <p class="text-sm text-gray-400">Aucune régularisation trouvée</p>
          </div>
        </div>

        <!-- Table -->
        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-100">
                <th class="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Réf</th>
                <th class="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Produit</th>
                <th class="px-5 py-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Quantité</th>
                <th class="px-5 py-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Stock actuel</th>
                <th class="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                <th class="px-5 py-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Statut</th>
                <th class="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Par</th>
                <th class="px-5 py-3 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in regularizations" :key="r.id"
                class="border-b border-gray-50 hover:bg-gray-50/50 transition-colors group">
                <!-- Ref -->
                <td class="px-5 py-3">
                  <span class="text-xs font-mono font-semibold text-primary">REG-{{ r.id }}</span>
                </td>
                <!-- Produit -->
                <td class="px-5 py-3">
                  <span class="text-sm font-medium text-gray-900">{{ r.product?.name || '-' }}</span>
                </td>
                <!-- Quantité -->
                <td class="px-5 py-3 text-center">
                  <span :class="[
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold',
                    (r.quantity || 0) < 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                  ]">
                    {{ r.quantity || '-' }}
                  </span>
                </td>
                <!-- Stock actuel -->
                <td class="px-5 py-3 text-center">
                  <span v-if="r.product && r.product.stock_quantity !== null && r.product.stock_quantity !== undefined"
                    :class="[
                      'text-sm font-semibold',
                      r.status === 'pending' && !canReconcile(r) ? 'text-red-500' : 'text-gray-900'
                    ]">
                    {{ r.product.stock_quantity }}
                  </span>
                  <span v-else class="text-xs text-gray-300">-</span>
                </td>
                <!-- Date -->
                <td class="px-5 py-3">
                  <span class="text-xs text-gray-500">{{ formatDate(r.created_at) }}</span>
                </td>
                <!-- Statut -->
                <td class="px-5 py-3 text-center">
                  <span :class="[
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                    getStatusBadgeClass(r.status)
                  ]">{{ getStatusLabel(r.status) }}</span>
                </td>
                <!-- Par -->
                <td class="px-5 py-3">
                  <div class="flex items-center gap-2">
                    <div class="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <i class="fas fa-user text-gray-400 text-[8px]"></i>
                    </div>
                    <span class="text-xs text-gray-600">{{ r.user?.name || '-' }}</span>
                  </div>
                </td>
                <!-- Actions -->
                <td class="px-5 py-3 text-right">
                  <div class="flex items-center justify-end gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                    <button v-if="r.status === 'pending'"
                      @click="openReconcileModal(r)"
                      :disabled="reconciling[r.id] || !canReconcile(r)"
                      :title="!canReconcile(r) ? getReconcileDisabledReason(r) : 'Régulariser'"
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <span v-if="reconciling[r.id]" class="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      <i v-else class="fas fa-check text-[9px]"></i>
                      Régulariser
                    </button>
                    <button v-if="r.product_id"
                      @click="viewProduct(r.product_id)"
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-primary bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors">
                      <i class="fas fa-eye text-[9px]"></i>
                      Voir
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <PaginationComponent
          :currentPage="pagination.page"
          :perPage="Number(pagination.per_page)"
          :total="pagination.total"
          :to="pagination.to || Math.min(pagination.page * Number(pagination.per_page), pagination.total)"
          :showPageNumbers="true"
          :maxVisiblePages="5"
          @page-change="goToPage"
        />
      </div>
    </div>

    <!-- Modal de réconciliation -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showReconcileModal"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="closeReconcileModal">
          <div class="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <!-- Header -->
            <div class="px-6 pt-6 pb-4">
              <div class="flex items-center gap-3 mb-1">
                <div class="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-exchange-alt text-emerald-500 text-sm"></i>
                </div>
                <div class="flex-1">
                  <h3 class="text-base font-bold text-gray-900">Régulariser le stock</h3>
                  <p class="text-[11px] text-gray-400">Confirmez la déduction de stock</p>
                </div>
                <button @click="closeReconcileModal"
                  class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                  <i class="fas fa-times text-xs"></i>
                </button>
              </div>
            </div>

            <!-- Body -->
            <div class="px-6 pb-4" v-if="selectedRegularization">
              <!-- Info card -->
              <div class="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                <div class="flex justify-between items-center">
                  <span class="text-xs text-gray-400">Référence</span>
                  <span class="text-xs font-mono font-semibold text-primary">REG-{{ selectedRegularization.id }}</span>
                </div>
                <div class="h-px bg-gray-200"></div>
                <div class="flex justify-between items-center">
                  <span class="text-xs text-gray-400">Produit</span>
                  <span class="text-xs font-semibold text-gray-900">{{ selectedRegularization.product?.name || '-' }}</span>
                </div>
                <div class="h-px bg-gray-200"></div>
                <div class="flex justify-between items-center">
                  <span class="text-xs text-gray-400">Quantité à retirer</span>
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-50 text-red-700">
                    -{{ Math.abs(selectedRegularization.quantity) }}
                  </span>
                </div>
                <div class="h-px bg-gray-200"></div>
                <div class="flex justify-between items-center">
                  <span class="text-xs text-gray-400">Stock actuel</span>
                  <span :class="[
                    'text-xs font-bold',
                    (selectedRegularization.product?.stock_quantity || 0) < Math.abs(selectedRegularization.quantity) ? 'text-red-500' : 'text-emerald-600'
                  ]">{{ selectedRegularization.product?.stock_quantity || 0 }}</span>
                </div>
              </div>

              <!-- Warning -->
              <div v-if="selectedRegularization.product && (selectedRegularization.product.stock_quantity || 0) < Math.abs(selectedRegularization.quantity)"
                class="flex items-start gap-2.5 px-3.5 py-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <i class="fas fa-exclamation-triangle text-amber-500 text-xs mt-0.5 flex-shrink-0"></i>
                <p class="text-[11px] text-amber-700 font-medium leading-relaxed">
                  Le stock actuel est insuffisant pour cette régularisation.
                </p>
              </div>

              <!-- Notes -->
              <div class="mb-4">
                <label class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Notes (optionnel)
                </label>
                <textarea
                  v-model="reconcileNotes"
                  rows="3"
                  placeholder="Ajouter des notes sur la réconciliation..."
                  class="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300 resize-none"
                ></textarea>
              </div>

              <!-- Info note -->
              <div class="flex items-start gap-2 px-3 py-2.5 bg-gray-50 rounded-lg mb-2">
                <i class="fas fa-info-circle text-gray-400 text-[10px] mt-0.5 flex-shrink-0"></i>
                <p class="text-[11px] text-gray-400 leading-relaxed">
                  Cette action va retirer la quantité du stock et marquer la régularisation comme réconciliée.
                </p>
              </div>
            </div>

            <!-- Footer -->
            <div class="px-6 pb-6 flex gap-3">
              <button
                @click="closeReconcileModal"
                :disabled="isReconciling"
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50">
                Annuler
              </button>
              <button
                @click="confirmReconcile"
                :disabled="isReconciling || (selectedRegularization && !canReconcile(selectedRegularization))"
                :title="selectedRegularization && !canReconcile(selectedRegularization) ? getReconcileDisabledReason(selectedRegularization) : ''"
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <span v-if="isReconciling" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block mr-1.5"></span>
                <i v-else class="fas fa-check text-[10px] mr-1"></i>
                {{ isReconciling ? 'Réconciliation...' : 'Confirmer' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import productRegularizationService from '@/services/productRegularizationService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'ProductRegularizationsView',
  components: { PaginationComponent },
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()
    const loading = ref(false)
    const regularizations = ref([])
    const searchQuery = ref('')
    const searchTimeout = ref(null)
    const showReconcileModal = ref(false)
    const selectedRegularization = ref(null)
    const reconcileNotes = ref('')
    const isReconciling = ref(false)
    const reconciling = ref({})
    const filters = ref({
      status: '',
      pending_only: true,
      per_page: 10
    })
    const pagination = ref({
      page: 1,
      per_page: 10,
      total: 0,
      from: 0,
      to: 0
    })

    const pendingCount = computed(() => {
      return regularizations.value.filter(r => r.status === 'pending').length
    })

    const reconciledCount = computed(() => {
      return regularizations.value.filter(r => r.status === 'reconciled').length
    })

    const cancelledCount = computed(() => {
      return regularizations.value.filter(r => r.status === 'cancelled').length
    })

    const fetchRegularizations = async () => {
      try {
        loading.value = true
        const res = await productRegularizationService.getRegularizations({
          status: filters.value.status || undefined,
          pending_only: !!filters.value.pending_only,
          per_page: filters.value.per_page,
          page: pagination.value.page,
          search: searchQuery.value.trim() || undefined
        })
        const data = res.data
        const list = Array.isArray(data) ? data : (data.data || data.items || [])
        regularizations.value = list
        pagination.value.total = Number(data.total || (data.meta && data.meta.total) || list.length) || 0
        pagination.value.per_page = Number(data.per_page || (data.meta && data.meta.per_page) || filters.value.per_page) || 10
        pagination.value.page = Number(data.current_page || (data.meta && data.meta.current_page) || pagination.value.page) || 1
        pagination.value.from = Number(data.from || (data.meta && data.meta.from) || ((pagination.value.page - 1) * pagination.value.per_page + 1)) || 0
        pagination.value.to = Number(data.to || (data.meta && data.meta.to) || Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total)) || 0
      } catch (e) {
        console.error('Erreur chargement régularisations:', e)
        regularizations.value = []
      } finally {
        loading.value = false
      }
    }

    const onFiltersChange = () => {
      pagination.value.page = 1
      fetchRegularizations()
    }

    const onSearchInput = () => {
      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(() => {
        pagination.value.page = 1
        fetchRegularizations()
      }, 500)
    }

    const goToPage = (page) => {
      pagination.value.page = page
      fetchRegularizations()
    }

    const viewProduct = (productId) => {
      router.push({ name: 'product-detail', params: { id: productId } })
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' })
    }

    const getStatusLabel = (status) => {
      const map = {
        pending: 'En attente',
        reconciled: 'Réconciliée',
        cancelled: 'Annulée'
      }
      return map[status] || status
    }

    const getStatusBadgeClass = (status) => {
      const map = {
        pending: 'bg-amber-50 text-amber-700',
        reconciled: 'bg-emerald-50 text-emerald-700',
        cancelled: 'bg-gray-100 text-gray-500'
      }
      return map[status] || 'bg-gray-100 text-gray-500'
    }

    const canReconcile = (regularization) => {
      if (regularization.status !== 'pending') {
        return false
      }

      if (!regularization.product) {
        return false
      }

      if (!regularization.product.track_inventory) {
        return false
      }

      const quantityToDeduct = Math.abs(regularization.quantity || 0)
      const currentStock = regularization.product.stock_quantity || 0

      if (currentStock < quantityToDeduct) {
        return false
      }

      return true
    }

    const getReconcileDisabledReason = (regularization) => {
      if (regularization.status !== 'pending') {
        return 'Cette régularisation a déjà été réconciliée ou annulée'
      }

      if (!regularization.product) {
        return 'Produit non trouvé'
      }

      if (!regularization.product.track_inventory) {
        return 'Ce produit ne suit pas l\'inventaire'
      }

      const quantityToDeduct = Math.abs(regularization.quantity || 0)
      const currentStock = regularization.product.stock_quantity || 0

      if (currentStock < quantityToDeduct) {
        return `Stock insuffisant (disponible: ${currentStock}, requis: ${quantityToDeduct})`
      }

      return ''
    }

    const openReconcileModal = (regularization) => {
      selectedRegularization.value = regularization
      reconcileNotes.value = ''
      showReconcileModal.value = true
    }

    const closeReconcileModal = () => {
      if (isReconciling.value) return
      showReconcileModal.value = false
      selectedRegularization.value = null
      reconcileNotes.value = ''
    }

    const confirmReconcile = async () => {
      if (!selectedRegularization.value) return

      const regularizationId = selectedRegularization.value.id
      isReconciling.value = true
      reconciling.value[regularizationId] = true

      try {
        await productRegularizationService.reconcileRegularization(regularizationId, {
          notes: reconcileNotes.value.trim()
        })

        toastStore.addToast({
          type: 'success',
          message: 'Régularisation réconciliée avec succès',
          duration: 3000
        })

        closeReconcileModal()
        fetchRegularizations()
      } catch (error) {
        console.error('Erreur lors de la réconciliation:', error)
        const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erreur lors de la réconciliation'

        toastStore.addToast({
          type: 'error',
          message: errorMessage,
          duration: 4000
        })
      } finally {
        isReconciling.value = false
        reconciling.value[regularizationId] = false
      }
    }

    // initial load
    fetchRegularizations()

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    return {
      loading,
      regularizations,
      searchQuery,
      filters,
      pagination,
      pendingCount,
      reconciledCount,
      cancelledCount,
      showReconcileModal,
      selectedRegularization,
      reconcileNotes,
      isReconciling,
      reconciling,
      fetchRegularizations,
      onFiltersChange,
      onSearchInput,
      goToPage,
      viewProduct,
      formatDate,
      getStatusLabel,
      getStatusBadgeClass,
      canReconcile,
      getReconcileDisabledReason,
      openReconcileModal,
      closeReconcileModal,
      confirmReconcile
    }
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.2s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
    transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
.modal-enter-from > div {
    transform: scale(0.95);
}
.modal-leave-to > div {
    transform: scale(0.95);
}
</style>
